import { useEffect, useState } from 'react';
import {
    LayoutDashboard, FileText, QrCode, Building2, Bell, BarChart3, Lock, Users, Workflow,
    CheckCircle2, ChevronRight, Smartphone, Globe, Zap, Database, TrendingUp,
    AlertCircle, FileCheck2, Mail
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Animated Demo: 4-scene story showing a real workflow week
// ─────────────────────────────────────────────────────────────────────────────
const SCENES = [
    {
        chapter: 'Lunes',
        caption: 'Andrea registra los servicios del mes',
        url: 'paysync.cl/servicios/mes',
    },
    {
        chapter: 'Miércoles',
        caption: 'Genera el EDP en PDF con un click',
        url: 'paysync.cl/servicios/edp',
    },
    {
        chapter: 'Jueves',
        caption: 'El cliente aprueba desde su celular',
        url: 'paysync.cl/edp/aprobar',
    },
    {
        chapter: 'Viernes',
        caption: 'Cierra el mes con la cartera bajo control',
        url: 'paysync.cl/dashboard',
    },
];

const DemoMockup = () => {
    const [step, setStep] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setStep((s) => (s + 1) % SCENES.length), 7500);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="relative mx-auto max-w-5xl">
            {/* Glow */}
            <div className="absolute -inset-8 bg-gradient-to-r from-[#3D7A7A]/30 via-[#5B9A9A]/20 to-[#3D7A7A]/30 blur-3xl rounded-[3rem] -z-10" />

            {/* Story caption */}
            <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 max-w-2xl mx-auto" key={step}>
                <span className="text-xs uppercase tracking-widest text-[#9DD5D5] font-bold flex-shrink-0" style={{ animation: 'fadeRow 0.8s' }}>
                    {SCENES[step].chapter}
                </span>
                <span className="h-px hidden sm:block flex-1 bg-gradient-to-r from-[#5B9A9A]/40 to-transparent" />
                <span className="text-sm md:text-base text-white/85" style={{ animation: 'fadeRow 1s 0.2s backwards' }}>
                    {SCENES[step].caption}
                </span>
            </div>

            {/* Browser frame */}
            <div className="rounded-xl md:rounded-2xl bg-[#0F1A1A] border border-white/10 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-4 py-2 md:py-3 border-b border-white/10 bg-[#0A1414]">
                    <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-400/70" />
                    <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-amber-400/70" />
                    <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-emerald-400/70" />
                    <div className="ml-2 md:ml-4 flex-1 max-w-md mx-auto bg-white/5 text-white/60 text-[9px] md:text-xs px-2 md:px-3 py-1 md:py-1.5 rounded-md border border-white/10 truncate">
                        {SCENES[step].url}
                    </div>
                </div>

                <div className="relative bg-white aspect-[4/5] sm:aspect-[16/10] md:aspect-[16/9]">
                    {/* ─── ESCENA 1: SERVICIOS DEL MES (Lunes) ─── */}
                    <div className={`absolute inset-0 transition-opacity duration-1000 ${step === 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                        <div className="p-3 md:p-6 bg-white h-full overflow-hidden" key={step === 0 ? 'a' : 'a-off'}>
                            <div className="flex items-start md:items-center justify-between mb-2 md:mb-4 gap-2 flex-col sm:flex-row">
                                <div>
                                    <h3 className="text-sm md:text-xl font-bold text-slate-900">Servicios de Abril 2026</h3>
                                    <p className="text-[9px] md:text-xs text-slate-500">Solo período seleccionado</p>
                                </div>
                                <div className="flex gap-1.5 md:gap-2 flex-wrap" style={{ animation: step === 0 ? 'slideUp 0.8s' : 'none' }}>
                                    <span className="px-1.5 md:px-2 py-0.5 md:py-1 text-[8px] md:text-[10px] rounded bg-[#E8F4F4] text-[#3D7A7A] font-bold">12 SERVICIOS</span>
                                    <span className="px-1.5 md:px-2 py-0.5 md:py-1 text-[8px] md:text-[10px] rounded bg-amber-100 text-amber-800 font-bold">$58.420.000</span>
                                </div>
                            </div>
                            <table className="w-full text-[9px] md:text-xs">
                                <thead>
                                    <tr className="border-b border-slate-200 text-slate-500 text-[8px] md:text-[10px] uppercase tracking-wide">
                                        <th className="text-left py-1.5 md:py-2">Servicio</th>
                                        <th className="text-left py-1.5 md:py-2 hidden sm:table-cell">Faena</th>
                                        <th className="text-left py-1.5 md:py-2 hidden md:table-cell">Cliente</th>
                                        <th className="text-right py-1.5 md:py-2">Monto</th>
                                        <th className="text-center py-1.5 md:py-2 hidden sm:table-cell">EDP</th>
                                        <th className="text-center py-1.5 md:py-2">Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        { srv: 'Asesoría Estructural', faena: 'Planta Norte', cli: 'Constructora Andes', mt: '$8.330.000', st: 'En Curso', col: 'blue' },
                                        { srv: 'Inspección Técnica', faena: 'Mina Cerro Verde', cli: 'TecnoSur Ingeniería', mt: '$5.420.000', st: 'En Curso', col: 'blue' },
                                        { srv: 'Servicios Contables', faena: 'Oficina Central', cli: 'Logística Pacífico', mt: '$4.150.000', st: 'En Curso', col: 'blue' },
                                        { srv: 'Auditoría Interna', faena: 'Edificio Corporativo', cli: 'Inversiones Maule', mt: '$9.840.000', st: 'En Curso', col: 'blue' },
                                    ].map((r, i) => (
                                        <tr key={i} className="border-b border-slate-100" style={{ animation: step === 0 ? `fadeRow 0.7s ${300 + i * 200}ms backwards` : 'none' }}>
                                            <td className="py-1.5 md:py-2 text-slate-800 font-medium truncate max-w-[120px]">{r.srv}</td>
                                            <td className="py-1.5 md:py-2 text-slate-600 hidden sm:table-cell">{r.faena}</td>
                                            <td className="py-1.5 md:py-2 text-slate-600 hidden md:table-cell">{r.cli}</td>
                                            <td className="py-1.5 md:py-2 text-right font-semibold whitespace-nowrap">{r.mt}</td>
                                            <td className="py-1.5 md:py-2 text-center hidden sm:table-cell">
                                                <span className="inline-flex items-center gap-1 text-[8px] md:text-[10px] px-1.5 md:px-2 py-0.5 rounded border border-[#5B9A9A] text-[#3D7A7A]">
                                                    <FileText size={9} /> Ver
                                                </span>
                                            </td>
                                            <td className="py-1.5 md:py-2 text-center">
                                                <span className={`text-[8px] md:text-[10px] px-1.5 md:px-2 py-0.5 rounded-full bg-${r.col}-100 text-${r.col}-700 font-medium whitespace-nowrap`}>{r.st}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {step === 0 && (
                                <div className="mt-2 md:mt-4 inline-flex items-center gap-1 md:gap-2 text-[8px] md:text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 md:py-1 rounded-full border border-emerald-200" style={{ animation: 'fadeRow 0.8s 1.5s backwards' }}>
                                    <CheckCircle2 size={11} /> 12 servicios listos para facturar este mes
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ─── ESCENA 2: EDP PDF (Miércoles) ─── */}
                    <div className={`absolute inset-0 transition-opacity duration-1000 ${step === 1 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                        <div className="h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-3 md:p-6">
                            <div className="bg-white shadow-2xl rounded-sm w-full max-w-xs sm:max-w-sm py-4 sm:py-6 px-4 sm:px-7 relative" style={{ animation: step === 1 ? 'slideUp 0.9s' : 'none' }}>
                                <div className="text-center pb-2 sm:pb-3 border-b-2 border-[#3D7A7A]">
                                    <h2 className="text-base sm:text-xl font-bold text-[#2D5A5A] tracking-wide">ESTADO DE PAGO</h2>
                                    <p className="text-[9px] sm:text-[10px] font-bold text-slate-900 mt-1 tracking-widest">CONSTRUCTORA NORTE</p>
                                    <p className="text-[7px] sm:text-[8px] text-slate-500 mt-0.5">FOLIO N° 0287</p>
                                </div>
                                <div className="mt-2 sm:mt-3 space-y-1 sm:space-y-1.5 text-[8px] sm:text-[9px]">
                                    <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-medium text-slate-800">TecnoSur Ingeniería SpA</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">Faena</span><span className="font-medium text-slate-800">Mina Cerro Verde</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">Período</span><span className="font-medium text-slate-800">Abril 2026</span></div>
                                </div>
                                <div className="bg-slate-50 rounded p-2 sm:p-2.5 mt-2 sm:mt-3 space-y-0.5 sm:space-y-1 text-[8px] sm:text-[9px]">
                                    <div className="flex justify-between"><span className="text-slate-500">Neto</span><span>$5.420.000</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">IVA 19%</span><span>$1.029.800</span></div>
                                    <div className="flex justify-between pt-1 border-t border-slate-200"><span className="font-bold">Total</span><span className="font-bold text-[#3D7A7A]">$6.449.800</span></div>
                                </div>
                                <div className="mt-2 sm:mt-3 flex items-center gap-2 sm:gap-3" style={{ animation: step === 1 ? 'fadeRow 1s 1.2s backwards' : 'none' }}>
                                    <div className="w-9 h-9 sm:w-12 sm:h-12 bg-[#2D5A5A] rounded-sm flex items-center justify-center flex-shrink-0">
                                        <QrCode size={24} className="text-white sm:hidden" />
                                        <QrCode size={32} className="text-white hidden sm:block" />
                                    </div>
                                    <div className="text-[7px] sm:text-[8px] text-slate-600 leading-tight">
                                        <p className="font-bold text-[#2D5A5A]">APROBACIÓN DEL CLIENTE</p>
                                        <p>Escanea el QR para revisar y aprobar este Estado de Pago en línea.</p>
                                    </div>
                                </div>
                                {step === 1 && (
                                    <div className="absolute -right-2 sm:-right-4 -top-2 sm:-top-4 bg-emerald-500 text-white text-[9px] sm:text-[10px] font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-lg flex items-center gap-1" style={{ animation: 'slideUp 0.6s 1.8s backwards' }}>
                                        <CheckCircle2 size={11} /> PDF listo
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ─── ESCENA 3: APROBACIÓN MÓVIL (Jueves) ─── */}
                    <div className={`absolute inset-0 transition-opacity duration-1000 ${step === 2 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                        <div className="h-full bg-gradient-to-br from-slate-50 to-[#E8F4F4] flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 p-3 sm:px-8 overflow-hidden">
                            {/* Phone mockup */}
                            <div className="bg-slate-900 rounded-[1.5rem] sm:rounded-[2rem] p-1.5 sm:p-2 shadow-2xl flex-shrink-0" style={{ animation: step === 2 ? 'slideUp 1s' : 'none' }}>
                                <div className="bg-white rounded-[1.2rem] sm:rounded-[1.6rem] w-36 sm:w-48 overflow-hidden">
                                    <div className="bg-slate-100 py-1.5 sm:py-2 text-center text-[7px] sm:text-[8px] text-slate-400">paysync.cl</div>
                                    <div className="p-2 sm:p-3">
                                        <p className="text-[7px] sm:text-[8px] text-slate-400 text-center uppercase">Aprobación EDP</p>
                                        <p className="text-center text-[9px] sm:text-[10px] font-bold text-[#2D5A5A] tracking-wider mt-1">CONSTRUCTORA NORTE</p>
                                        <p className="text-center text-sm sm:text-base font-bold text-slate-900">EDP N° 0287</p>
                                        <p className="text-center text-[7px] sm:text-[8px] text-slate-500">Abril 2026</p>
                                        <div className="bg-slate-50 rounded p-1.5 sm:p-2 mt-2 text-[7px] sm:text-[8px] space-y-0.5">
                                            <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span>TecnoSur</span></div>
                                            <div className="flex justify-between font-bold pt-1 border-t"><span>Total</span><span className="text-[#3D7A7A]">$6.449.800</span></div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-1 mt-2">
                                            <div className="border-2 border-emerald-500 bg-emerald-50 rounded py-1.5 sm:py-2 text-center text-emerald-700 text-[8px] sm:text-[9px] font-bold flex flex-col items-center" style={{ animation: step === 2 ? 'pulse 2s 2s infinite' : 'none' }}>
                                                <CheckCircle2 size={12} className="mb-0.5" />
                                                Aprobar
                                            </div>
                                            <div className="border border-slate-200 rounded py-1.5 sm:py-2 text-center text-slate-400 text-[8px] sm:text-[9px] font-bold flex flex-col items-center">
                                                <span className="text-sm leading-none">×</span>
                                                Rechazar
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Side annotations */}
                            <div className="flex-1 max-w-xs sm:max-w-sm space-y-1.5 sm:space-y-3 w-full">
                                <div className="bg-white border border-slate-200 rounded-lg p-2 sm:p-3 shadow-sm" style={{ animation: step === 2 ? 'fadeRow 0.8s 0.8s backwards' : 'none' }}>
                                    <p className="text-[8px] sm:text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Paso 1</p>
                                    <p className="text-[10px] sm:text-xs text-slate-700 mt-0.5">El cliente recibe el PDF y escanea el QR.</p>
                                </div>
                                <div className="bg-white border border-slate-200 rounded-lg p-2 sm:p-3 shadow-sm" style={{ animation: step === 2 ? 'fadeRow 0.8s 1.5s backwards' : 'none' }}>
                                    <p className="text-[8px] sm:text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Paso 2</p>
                                    <p className="text-[10px] sm:text-xs text-slate-700 mt-0.5">Revisa, comenta y aprueba en segundos.</p>
                                </div>
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 sm:p-3 shadow-sm" style={{ animation: step === 2 ? 'fadeRow 0.8s 2.5s backwards' : 'none' }}>
                                    <p className="text-[8px] sm:text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1"><Bell size={10} /> Tu equipo se entera al instante</p>
                                    <p className="text-[10px] sm:text-xs text-emerald-900 mt-0.5">Estado del servicio actualizado automáticamente.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ─── ESCENA 4: DASHBOARD (Viernes) ─── */}
                    <div className={`absolute inset-0 transition-opacity duration-1000 ${step === 3 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                        <div className="p-3 md:p-6 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 h-full bg-gradient-to-br from-slate-50 to-[#E8F4F4] overflow-hidden">
                            <div className="col-span-2 md:col-span-4 flex items-end justify-between">
                                <div>
                                    <p className="text-[9px] md:text-xs text-slate-400 uppercase tracking-wide">Dashboard · Constructora Norte</p>
                                    <h3 className="text-sm md:text-xl font-bold text-slate-900">Resumen del mes</h3>
                                </div>
                                <span className="text-[8px] md:text-[10px] text-emerald-700 bg-emerald-100 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full font-medium whitespace-nowrap" style={{ animation: step === 3 ? 'fadeRow 0.8s 0.5s backwards' : 'none' }}>+ 18% vs mes anterior</span>
                            </div>
                            {[
                                { label: 'Facturación', val: '$58.420.000', color: '#3D7A7A', icon: BarChart3, t: 200, w: 80 },
                                { label: 'IVA', val: '$9.328.000', color: '#D97706', icon: FileText, t: 400, w: 70 },
                                { label: 'Por cobrar', val: '$12.850.000', color: '#DC2626', icon: AlertCircle, t: 600, w: 35 },
                                { label: 'Recuperado', val: '$8.420.000', color: '#059669', icon: Zap, t: 800, w: 90 },
                            ].map((k, i) => (
                                <div key={i} className="bg-white rounded-md md:rounded-lg p-2 md:p-3 border border-slate-200 shadow-sm" style={{ animation: step === 3 ? `slideUp 0.8s ${k.t}ms backwards` : 'none' }}>
                                    <div className="flex items-center justify-between mb-1 md:mb-2">
                                        <p className="text-[8px] md:text-[9px] text-slate-500 uppercase truncate">{k.label}</p>
                                        <k.icon size={12} style={{ color: k.color }} />
                                    </div>
                                    <p className="text-[11px] md:text-base font-bold text-slate-900 truncate">{k.val}</p>
                                    <div className="h-1 w-full bg-slate-100 rounded-full mt-1 md:mt-2 overflow-hidden">
                                        <div className="h-full rounded-full" style={{ width: `${k.w}%`, background: k.color, animation: step === 3 ? `growBar 1.2s ${k.t + 400}ms backwards` : 'none' }} />
                                    </div>
                                </div>
                            ))}
                            <div className="col-span-2 md:col-span-4 bg-white rounded-md md:rounded-lg p-2 md:p-4 border border-slate-200 shadow-sm overflow-hidden" style={{ animation: step === 3 ? 'fadeRow 0.8s 1.5s backwards' : 'none' }}>
                                <p className="text-[10px] md:text-xs font-semibold text-slate-700 mb-1.5 md:mb-3">Servicios cobrados esta semana</p>
                                <div className="space-y-1 md:space-y-2">
                                    {[
                                        { srv: 'Inspección Técnica · TecnoSur', mt: '$6.449.800' },
                                        { srv: 'Asesoría Estructural · Constructora Andes', mt: '$9.912.700' },
                                        { srv: 'Auditoría Interna · Inversiones Maule', mt: '$11.709.600' },
                                    ].map((s, i) => (
                                        <div key={i} className="flex items-center justify-between text-[9px] md:text-xs py-1 md:py-1.5 border-b border-slate-100 last:border-0 gap-2">
                                            <span className="text-slate-700 truncate flex-1 min-w-0">{s.srv}</span>
                                            <span className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                                                <span className="font-semibold whitespace-nowrap">{s.mt}</span>
                                                <span className="text-[8px] md:text-[10px] px-1.5 md:px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Pagado</span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination dots */}
            <div className="flex justify-center gap-2 mt-6">
                {SCENES.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setStep(i)}
                        className={`h-1.5 rounded-full transition-all ${step === i ? 'w-10 bg-[#3D7A7A]' : 'w-2 bg-slate-300 hover:bg-slate-400'}`}
                    />
                ))}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function Propuesta() {
    const [config, setConfig] = useState(null);
    useEffect(() => {
        axios.get(`${API_URL}/api/configuracion/public`).then((r) => setConfig(r.data)).catch(() => {});
    }, []);

    const features = [
        { icon: Building2, title: 'Multi-tenant', desc: 'Cada empresa gestiona su cartera de manera totalmente independiente. Datos aislados, configuración propia.' },
        { icon: LayoutDashboard, title: 'Dashboard en vivo', desc: 'KPIs accionables: facturación, IVA, por cobrar, cobranza recuperada y mora >30 días, todos clickeables.' },
        { icon: Workflow, title: 'Workflow de cobranza', desc: 'Estados configurables del proceso: en curso, EDP enviado, aprobado, facturado, pendiente, vencido, en mora, pagado.' },
        { icon: FileCheck2, title: 'EDP con PDF profesional', desc: 'Genera Estados de Pago en PDF con tu logo, datos bancarios, faena y comentarios. Folio correlativo automático.' },
        { icon: QrCode, title: 'Aprobación por QR', desc: 'Cada EDP incluye un QR único. El cliente escanea, revisa y aprueba o rechaza desde su celular en segundos.' },
        { icon: Bell, title: 'Alertas inteligentes', desc: 'Notificaciones automáticas de servicios próximos a vencer, EDP sin respuesta y facturas en mora.' },
        { icon: BarChart3, title: 'Aging y reportes', desc: 'Análisis automático del envejecimiento de tu cartera. Identifica deudores críticos al instante.' },
        { icon: Globe, title: 'Indicadores en vivo', desc: 'UF, USD y Cobre actualizados en tiempo real desde mindicador.cl, integrados al header de la plataforma.' },
        { icon: Users, title: 'Roles y permisos', desc: 'Superadmin, administrador de empresa y usuarios internos. Cada uno con acceso solo a lo que le corresponde.' },
        { icon: Database, title: 'Trazabilidad total', desc: 'Historial completo de cada acción: quién hizo qué y cuándo. Ideal para auditoría y respaldo legal.' },
        { icon: Lock, title: 'Seguridad empresarial', desc: 'Autenticación JWT, contraseñas hasheadas y aislamiento estricto de datos por tenant.' },
        { icon: Smartphone, title: 'Responsive', desc: 'Funciona en celular, tablet y escritorio. Aprobaciones de clientes desde cualquier dispositivo.' },
    ];

    return (
        <div className="min-h-screen bg-[#0A1414] text-white">
            <style>{`
                @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes fadeRow { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
                @keyframes growBar { from { width: 0%; } }
                @keyframes pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); } 50% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); } }
                @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
                @keyframes shine { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
                .reveal { animation: fadeIn 0.8s backwards; }
                .float { animation: float 4s ease-in-out infinite; }
                .grain::before { content: ''; position: absolute; inset: 0; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); opacity: 0.4; pointer-events: none; }
            `}</style>

            {/* ───── HEADER ───── */}
            <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0A1414]/80 border-b border-white/5">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {config?.logo_plataforma_url ? (
                            <img src={`${API_URL}${config.logo_plataforma_url}`} alt="PaySync" className="h-8 w-auto brightness-0 invert" />
                        ) : (
                            <span className="text-xl font-bold tracking-tight">{config?.nombre_plataforma || 'PaySync'}</span>
                        )}
                    </div>
                    <span className="text-xs text-white/50 hidden md:block">Plataforma de Cobranza Inteligente</span>
                </div>
            </header>

            {/* ───── HERO ───── */}
            <section className="relative overflow-hidden grain">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3D7A7A]/20 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#5B9A9A]/15 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3" />

                <div className="max-w-6xl mx-auto px-6 pt-20 pb-16 relative">
                    <div className="reveal" style={{ animationDelay: '0.1s' }}>
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5B9A9A]/15 border border-[#5B9A9A]/30 text-xs text-[#9DD5D5]">
                            <Zap size={12} /> Cobranza inteligente para empresas chilenas
                        </span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight mt-6 leading-[1.05] reveal" style={{ animationDelay: '0.2s' }}>
                        Cobra antes,<br />
                        <span className="bg-gradient-to-r from-[#5B9A9A] via-[#9DD5D5] to-[#5B9A9A] bg-clip-text text-transparent" style={{ backgroundSize: '200% 100%', animation: 'shine 4s linear infinite' }}>
                            sin perseguir clientes.
                        </span>
                    </h1>
                    <p className="text-lg md:text-xl text-white/70 max-w-2xl mt-6 leading-relaxed reveal" style={{ animationDelay: '0.3s' }}>
                        PaySync centraliza la gestión de tus servicios facturables, automatiza el envío de Estados de Pago
                        con QR de aprobación y te da visibilidad total de tu cartera. Tu equipo deja de hacer planillas y
                        empieza a cobrar.
                    </p>

                    <div className="flex flex-wrap gap-3 mt-10 reveal" style={{ animationDelay: '0.4s' }}>
                        <Stat value="60%" label="menos tiempo en cobranza" />
                        <Stat value="3x" label="velocidad de aprobación EDP" />
                        <Stat value="100%" label="trazabilidad" />
                        <Stat value="0" label="planillas Excel" />
                    </div>
                </div>

                {/* Demo */}
                <div className="max-w-6xl mx-auto px-6 pb-20 relative">
                    <DemoMockup />
                </div>
            </section>

            {/* ───── PROBLEM ───── */}
            <section className="py-20 bg-gradient-to-b from-[#0A1414] to-[#0F1A1A] border-t border-white/5">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">Lo que hoy frena tu cobranza.</h2>
                    <p className="text-white/60 max-w-2xl mb-12">Si reconoces alguno de estos dolores, PaySync los resuelve.</p>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { t: 'Excel descontrolado', d: 'Múltiples planillas, versiones distintas, datos que no cuadran entre el área comercial y contabilidad.' },
                            { t: 'EDP enviados a ciegas', d: 'No sabes si el cliente recibió, leyó, aprobó o rechazó tu Estado de Pago. Solo te enteras cuando llamas.' },
                            { t: 'Mora descubierta tarde', d: 'Te das cuenta de los vencidos solo cuando alguien revisa manualmente. La cartera envejece sin alertas.' },
                        ].map((p, i) => (
                            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
                                <AlertCircle className="text-amber-400 mb-3" size={22} />
                                <h3 className="font-semibold text-lg mb-2">{p.t}</h3>
                                <p className="text-sm text-white/60 leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ───── FEATURES GRID ───── */}
            <section className="py-24 bg-[#0F1A1A] border-t border-white/5">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="max-w-2xl mb-16">
                        <span className="text-xs uppercase tracking-widest text-[#9DD5D5] font-semibold">Funcionalidades</span>
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-3">Todo lo que necesitas para cobrar a tiempo.</h2>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden">
                        {features.map((f, i) => (
                            <div key={i} className="bg-[#0F1A1A] p-7 hover:bg-white/5 transition-all group">
                                <div className="w-10 h-10 rounded-lg bg-[#3D7A7A]/15 border border-[#5B9A9A]/30 flex items-center justify-center mb-4 group-hover:bg-[#3D7A7A]/30 transition-all">
                                    <f.icon size={18} className="text-[#9DD5D5]" />
                                </div>
                                <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                                <p className="text-sm text-white/55 leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ───── WORKFLOW ───── */}
            <section className="py-24 bg-gradient-to-b from-[#0F1A1A] to-[#0A1414] border-t border-white/5">
                <div className="max-w-5xl mx-auto px-6">
                    <span className="text-xs uppercase tracking-widest text-[#9DD5D5] font-semibold">Cómo funciona</span>
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-3 mb-16">El flujo completo, automatizado.</h2>

                    <div className="space-y-px">
                        {[
                            { n: '01', icon: FileText, t: 'Registra el servicio', d: 'Cliente, faena, monto neto, condición de pago. PaySync calcula IVA y vencimiento automáticamente. Si es recurrente, replica los períodos por ti.' },
                            { n: '02', icon: FileCheck2, t: 'Genera el EDP con un click', d: 'Tu Estado de Pago en PDF profesional con folio correlativo, datos bancarios, faena y código QR único. Listo para enviar.' },
                            { n: '03', icon: QrCode, t: 'El cliente aprueba desde su celular', d: 'Escanea el QR del PDF, revisa los datos en una página optimizada para móvil y aprueba o rechaza con un comentario.' },
                            { n: '04', icon: Bell, t: 'Tu equipo se entera al instante', d: 'El estado del servicio cambia automáticamente. Si es rechazo, ves el comentario del cliente y el motivo en el historial.' },
                            { n: '05', icon: TrendingUp, t: 'Cobras y mides', d: 'Registra el pago, marca el servicio como pagado y mira en el dashboard cuánto recuperaste —incluyendo lo que estaba en mora.' },
                        ].map((s, i) => (
                            <div key={i} className="flex gap-6 items-start py-8 border-b border-white/5 last:border-0 reveal" style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className="flex-shrink-0">
                                    <div className="text-4xl md:text-5xl font-bold text-white/15 leading-none">{s.n}</div>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <s.icon size={18} className="text-[#9DD5D5]" />
                                        <h3 className="text-xl md:text-2xl font-semibold">{s.t}</h3>
                                    </div>
                                    <p className="text-white/60 leading-relaxed max-w-2xl">{s.d}</p>
                                </div>
                                <ChevronRight size={20} className="text-white/20 mt-2 hidden md:block" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ───── VALOR ───── */}
            <section className="py-24 bg-[#0A1414] border-t border-white/5 relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#3D7A7A]/15 rounded-full blur-3xl" />
                <div className="max-w-6xl mx-auto px-6 relative">
                    <div className="text-center mb-16">
                        <span className="text-xs uppercase tracking-widest text-[#9DD5D5] font-semibold">Propuesta de valor</span>
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-3 max-w-3xl mx-auto">
                            Reduce el ciclo de cobranza. Aumenta el flujo de caja.
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { t: 'Para Gerencia', items: ['Visibilidad en tiempo real de la cartera', 'Métricas de performance del proceso', 'Aging automático sin armar reportes'] },
                            { t: 'Para Cobranza', items: ['EDP en PDF con un click, sin diseñador', 'Confirmación digital del cliente vía QR', 'Workflow guiado, sin pasos olvidados'] },
                            { t: 'Para Contabilidad', items: ['IVA del período calculado automáticamente', 'Folios correlativos sin gestión manual', 'Historial auditable por cada movimiento'] },
                        ].map((b, i) => (
                            <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-[#5B9A9A]/40 transition-all">
                                <h3 className="text-xl font-bold mb-5">{b.t}</h3>
                                <ul className="space-y-3">
                                    {b.items.map((it, j) => (
                                        <li key={j} className="flex items-start gap-3 text-sm text-white/70">
                                            <CheckCircle2 size={16} className="text-[#5B9A9A] flex-shrink-0 mt-0.5" />
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ───── CIERRE ───── */}
            <section className="py-32 bg-gradient-to-b from-[#0F1A1A] via-[#0A1414] to-black border-t border-white/5 relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-[800px] h-[800px] bg-[#3D7A7A]/10 rounded-full blur-3xl" />
                </div>
                <div className="max-w-3xl mx-auto px-6 text-center relative">
                    <Mail className="mx-auto text-[#9DD5D5] mb-6" size={32} />
                    <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-[1.1]">
                        Imagina cerrar el mes <span className="text-[#9DD5D5]">sin estrés</span> en tu equipo de cobranza.
                    </h2>
                    <p className="text-lg text-white/60 leading-relaxed">
                        PaySync ya está implementado y funcionando. Solo falta que decidas dejar atrás las planillas
                        y empezar a cobrar con foco. Te lo mostramos en una demo personalizada cuando quieras.
                    </p>
                </div>
            </section>

            {/* ───── FOOTER ───── */}
            <footer className="border-t border-white/5 py-10 bg-black">
                <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-xs text-white/40">© {new Date().getFullYear()} {config?.nombre_plataforma || 'PaySync'}. Todos los derechos reservados.</p>
                    <p className="text-xs text-white/40">Plataforma de cobranza inteligente</p>
                </div>
            </footer>
        </div>
    );
}

const Stat = ({ value, label }) => (
    <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur">
        <p className="text-2xl font-bold tracking-tight text-[#9DD5D5]">{value}</p>
        <p className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">{label}</p>
    </div>
);
